import { useParams, useNavigate, useLocation } from "react-router-dom";
import SectionAppBar from "../../common/SectionAppBar";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBom } from "../../redux/partSlice";
import {
  getProcedureRevisionsForPart,
  getTrainingLogForPart,
} from "../../redux/procedureRevisionSlice";
import { getAllSectionsForPart } from "../../redux/sectionSlice";
import {
  getAllStepsForSection,
  getAllSubstepsForStep,
  getAllRichTextsForStep,
  getAllRichTextsForSubstep,
  getAllFormsForStep,
  getAllFormsForSubstep,
  getAllAttachmentsForStep,
  getAllAttachmentsForSubstep,
  getAllCanvasesForStep,
  getAllCanvasesForSubstep,
  getAllVideosForStep,
  getAllVideosForSubstep,
} from "../../redux/stepSlice";
import { formatName } from "../../util/AppUtils.jsx";
import SetTaskInfo from "../Tasks/SetTaskInfo.js";
import { getPages, renderPreview, renderVideo } from "./plugins/utils.js";
import {
  applyTransparency,
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../SectionElements/plugins/utils.js";
import CanvasDisplay from "../Canvas/components/canvas-core/CanvasDisplay.jsx";
import RectangleElement from "../Canvas/components/rectangle/RectangleElement.jsx";
import ArrowElement from "../Canvas/components/arrow/ArrowElement.jsx";
import CircleElement from "../Canvas/components/circle/CircleElement.jsx";
import LineElement from "../Canvas/components/line/LineElement.jsx";
import CanvasImage from "../Canvas/components/canvas-core/CanvasImage.jsx";
import TextElement from "../Canvas/components/text/TextElement.jsx";
import DoubleSidedArrowElement from "../Canvas/components/arrow/DoubleSidedArrowElement.jsx";
import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import Loader from "../../common/Loader.js";

const SelectedPartPage = () => {
  const {
    partId,
    title,
    revisionId,
    partNumber,
    revision,
    siblingOrder,
    type,
  } = useParams();
  const location = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  let richTextCounter = 1;
  let canvasCounter = 1;
  let videoCounter = 1;
  let formCounter = 1;
  let substepCounter = 1;
  let stepCounter = 1;
  let richTextsCounter = {};
  let attachmentCounter = 1;

  const { data } = location.state || {};
  // Redux state
  const { sections } = useSelector((state) => state.section);
  const { bomTable } = useSelector((state) => state.parts);
  const { revisionData, trainingLogs } = useSelector(
    (state) => state.procedureRevision
  );
  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        await Promise.all([
          dispatch(getProcedureRevisionsForPart({ partId })),
          dispatch(getTrainingLogForPart({ partId })),
          dispatch(getAllSectionsForPart({ partId, revisionId })),
          dispatch(getBom({ partId, revisionId })),
        ]);
      } catch (error) {
        console.error("Error fetching part data:", error);
        setErrorMessage("Failed to load part data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch, partId, revisionId]);

  // Fetch steps when sections are loaded
  useEffect(() => {
    if (sections.length > 0) {
      sections.forEach(async (section) => {
        await dispatch(
          getAllStepsForSection({
            partId,
            revisionId,
            sectionId: section.section_tree_node_id,
          })
        );
      });
    }
  }, [sections, dispatch, partId, revisionId]);

  // Fetch step content and substeps when steps are loaded
  useEffect(() => {
    if (Object.keys(stepsBySection).length > 0) {
      Object.entries(stepsBySection).forEach(([sectionId, steps]) => {
        steps.forEach((step) => {
          const { step_tree_node_id: stepId } = step;

          // Fetch for step-level content
          dispatch(
            getAllRichTextsForStep({ partId, revisionId, sectionId, stepId })
          );
          dispatch(
            getAllFormsForStep({ partId, revisionId, sectionId, stepId })
          );
          dispatch(
            getAllVideosForStep({ partId, revisionId, sectionId, stepId })
          );
          dispatch(
            getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId })
          );
          dispatch(
            getAllCanvasesForStep({ partId, revisionId, sectionId, stepId })
          );

          // Fetch substeps (async)
          dispatch(
            getAllSubstepsForStep({ partId, revisionId, sectionId, stepId })
          ).then((action) => {
            const substeps = action.payload?.substeps || [];
            substeps.forEach((substep) => {
              const substepId = substep.sub_step_tree_node_id;

              dispatch(
                getAllRichTextsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllFormsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllVideosForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllAttachmentsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllCanvasesForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
            });
          });
        });
      });
    }
  }, [sections, dispatch, partId, revisionId, stepsBySection]);

  // Sort sections alphabetically
  const sortedSections = [...sections];

  // Build stepGlobalNumbers map
  let globalStepCounter = 1;
  const stepGlobalNumbers = {}; // { step_tree_node_id: globalStepNumber }

  sortedSections.forEach((section) => {
    const steps = stepsBySection[section.section_tree_node_id] || [];
    steps.forEach((step) => {
      stepGlobalNumbers[step.step_tree_node_id] = globalStepCounter++;
    });
  });

  const getAdjustedSiblingOrder = (parentOrder, children) => {
    // Safety check: ensure children is an array
    const childrenArray = children || [];

    const validSiblingOrders = childrenArray
      .map(
        (child) => child.sibling_order || child.procedure_tree?.sibling_order
      )
      .filter(Boolean)
      .map((order) => parseInt(order, 10));

    if (validSiblingOrders.length === 0) {
      // No children, return parent + 1
      return `${parseInt(parentOrder) + 1}, 1`;
    }
    // Get the highest sibling order and increment it
    const highestOrder = Math.max(...validSiblingOrders);

    return `${parseInt(parentOrder) + 1}, ${(highestOrder + 1).toString()}`;
  };
  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  // Show loader while data is loading
  if (isLoading) {
    return <Loader text="Loading part data..." />;
  }

  // Show error if there's an issue
  if (errorMessage) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Part Data</h4>
          <p>{errorMessage}</p>
          <hr />
          <p className="mb-0">
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </p>
        </div>
      </div>
    );
  }

  let currrev = revisionData.find(
    (r) => r.procedure_revision_id === revisionId
  );
  if (currrev) {
    SetTaskInfo(
      currrev.procedure_revision_id,
      currrev.tree_node_details.tree_node_id
    );
  }
  return (
    <div>
      <SectionAppBar
        title={title}
        isSection={true}
        path={`/add-section`}
        siblingOrder={siblingOrder}
        partId={partId}
        revisionId={revisionId}
        isPart={true}
        revision={revision}
        partNumber={partNumber}
        sectionId="null"
        section={`Section ${siblingOrder}`}
        stepTitle="1"
        isSelectedPage={true}
        partType={type}
        data={data}
      />

      {/* Procedure Name and Checkbox */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 20px",
          flexWrap: "wrap",
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            <input
              type="checkbox"
              checked={true}
              // disabled={true}
            />
            <span style={{ fontSize: "16px", fontWeight: 600 }}>{title}</span>
          </label>

          {/* Description */}
          {/* <span style={{ marginLeft: "24px", marginTop: "5px", color: "#666" }}>
            Description: {revisionData?.[0]?.description || "No description available"}
          </span> */}
        </div>

        {/* Right-aligned section */}
      </div>

      {/* Display Revisions Table */}
      <div style={{ padding: "20px" }}>
        <h3 style={{ marginBottom: "-7px" }}>Revisions Table</h3>
        <table className="table" style={{ marginTop: "10px" }}>
          <thead>
            <tr>
              <th>Rev</th>
              <th>Date Released</th>
              <th>Revision Details</th>
              <th>Submitter / Requestor</th>
              <th>Approver</th>
              <th>References</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {[...revisionData]
              .sort((a, b) => {
                const parseRevision = (rev) => {
                  // Handle special case for "-" revision - should appear first
                  if (rev === "-") {
                    return {
                      base: "",
                      type: "special",
                      minorNum: -1,
                      amendChar: "",
                      priority: 0,
                    };
                  }

                  const match = rev.match(/^([A-Z])(?:-(\d*)|(\d+)([a-z]?))?$/);
                  if (!match) return null;
                  const [, letter, dashMinor, num, amendChar] = match;
                  return {
                    base: letter,
                    minorNum:
                      dashMinor !== undefined ? 0 : parseInt(num || "0"),
                    amendChar: amendChar || "",
                    priority: 1,
                  };
                };

                const revA = parseRevision(a.rev);
                const revB = parseRevision(b.rev);

                // Handle cases where parsing fails
                if (!revA && !revB) return 0;
                if (!revA) return 1; // Put unparseable revisions at the end
                if (!revB) return -1; // Put unparseable revisions at the end

                // "-" revision should always come first
                if (revA.priority === 0) return -1;
                if (revB.priority === 0) return 1;

                // Normal sorting for other revisions
                if (revA.base !== revB.base)
                  return revA.base < revB.base ? -1 : 1;
                if (revA.minorNum !== revB.minorNum)
                  return revA.minorNum - revB.minorNum;
                return revA.amendChar.localeCompare(revB.amendChar);
              })
              .map((rev, index) => {
                // Flatten all required_by entries from status_changes
                const requiredBy = "required_by" in rev ? rev.required_by : [];

                // Find users by purpose
                const submitter = requiredBy.find(
                  (rb) => rb.purpose?.toLowerCase() === "submit"
                )?.user;
                const approvers = requiredBy
                  .filter((rb) => rb.purpose?.toLowerCase() === "approve")
                  .map((rb) => rb.user)
                  .filter(Boolean);

                return (
                  <tr key={index}>
                    <td>{rev.rev || "-"}</td>
                    <td>
                      {rev.date_released
                        ? new Date(rev.date_released).toLocaleDateString(
                            "ja-JP",
                            {
                              year: "numeric",
                              month: "numeric",
                              day: "numeric",
                            }
                          )
                        : "-"}
                    </td>
                    <td>{rev.revision_details || "-"}</td>
                    <td>
                      {(() => {
                        const currentStatus = rev.status_changes?.find(
                          (sc) => sc.is_latest
                        )?.status_changed_to;
                        if (currentStatus === "MAINTAINED OUTSIDE OF SYSTEM") {
                          return "MAINTAINED OUTSIDE OF SYSTEM";
                        }

                        const submitterEntries = requiredBy.filter(
                          (rb) => rb.purpose?.toLowerCase() === "submit"
                        );
                        if (submitterEntries.length === 0) return "-";

                        return submitterEntries.map((rb, i) => {
                          const name = formatName(rev, rb.user);
                          return (
                            <div key={i}>
                              {!rb.is_open
                                ? `Submitted by ${name}`
                                : `Pending Submission from ${name}`}
                            </div>
                          );
                        });
                      })()}
                    </td>

                    <td>
                      {(() => {
                        const currentStatus = rev.status_changes?.find(
                          (sc) => sc.is_latest
                        )?.status_changed_to;
                        if (currentStatus === "MAINTAINED OUTSIDE OF SYSTEM") {
                          return "MAINTAINED OUTSIDE OF SYSTEM";
                        }

                        const approverEntries = requiredBy.filter(
                          (rb) => rb.purpose?.toLowerCase() === "approve"
                        );
                        if (approverEntries.length === 0) return "-";

                        return approverEntries.map((rb, i) => {
                          const name = formatName(rev, rb.user);
                          return (
                            <div key={i}>
                              {!rb.is_open
                                ? `Approved by ${name}`
                                : `Pending Approval from ${name}`}
                            </div>
                          );
                        });
                      })()}
                    </td>
                    <td>{rev.reference || "-"}</td>
                    <td>
                      {rev.status_changes?.find((sc) => sc.is_latest)
                        ?.status_changed_to || "-"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {/* Display Training Log */}
        <h3>Training Log</h3>
        <table className="table" style={{ marginTop: "-12px" }}>
          <thead>
            <tr>
              <th>Date of Training</th>
              <th>Trainer</th>
              <th>Trainer Signature</th>
              <th>Trainee</th>
              <th>Trainee Signature</th>
              <th>Revision Trained</th>
              <th>Training Status of Trainee</th>
            </tr>
          </thead>
          <tbody>
            {trainingLogs.map((row, index) => (
              <tr key={index}>
                <td>{new Date(row.date_of_training).toLocaleDateString()}</td>
                <td>
                  {row.trainer
                    ? `${row.trainer.first_name} ${row.trainer.last_name}`
                    : "N/A"}
                </td>
                <td>
                  {row.trainer_signature?.data ? (
                    <img
                      src={`data:image/png;base64,${btoa(
                        String.fromCharCode(
                          ...new Uint8Array(row.trainer_signature.data)
                        )
                      )}`}
                      alt="Trainer Signature"
                      className="signature"
                    />
                  ) : (
                    "No Signature"
                  )}
                </td>
                <td>
                  {row.trainee
                    ? `${row.trainee.first_name} ${row.trainee.last_name}`
                    : "N/A"}
                </td>
                <td>
                  {row.trainee_signature?.data ? (
                    <img
                      src={`data:image/png;base64,${btoa(
                        String.fromCharCode(
                          ...new Uint8Array(row.trainee_signature.data)
                        )
                      )}`}
                      alt="Trainee Signature"
                      className="signature"
                    />
                  ) : (
                    "No Signature"
                  )}
                </td>
                <td>{row.version_id_ref || "N/A"}</td>
                <td>
                  {row.is_maintained_outside_system
                    ? "Maintained Outside System"
                    : "In System"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Display BOM */}
        <h3>{type} BOM</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Item Code</th>
              <th>Description</th>
              <th>Qty Per</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            {bomTable.map((rev, index) => (
              <tr key={index}>
                <td>{rev.type_name}</td>
                <td>{rev.part_number}</td>
                <td>
                  <RenderHtml content={rev.part_description} />{" "}
                </td>
                <td>{rev.qty}</td>
                <td>{rev.unit_name}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Display Sections, Steps, and Content */}
        {sortedSections
          .sort((a, b) => {
            const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
            const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

            const orderCompare = compareSiblingOrders(orderA, orderB);
            if (orderCompare !== 0) return orderCompare;

            // Fallback: compare created_at
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA; // latest comes first
          })
          .map((section, index) => {
            return (
              <div
                key={section.section_tree_node_id}
                style={{
                  margin: "20px 0",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input type="checkbox" disabled={true} checked={true} />

                  <h2 style={{ margin: 0 }}>Section {index + 1}</h2>
                </label>
                <hr />
                <hr />
                <div>{section.section_name}</div>
                <div>{section.short_description}</div>

                {/* Steps for this section */}
                {(stepsBySection[section.section_tree_node_id] || []).map(
                  (step, stepIndex) => {
                    const stepId = step.step_tree_node_id;
                    const sectionId = section.section_tree_node_id;
                    stepCounter++;
                    const stepsBeforeThisSection = sortedSections
                      .slice(0, index)
                      .reduce(
                        (acc, s) =>
                          acc +
                          (stepsBySection[s.section_tree_node_id]?.length || 0),
                        0
                      );
                    const globalStepNumber =
                      stepsBeforeThisSection + stepIndex + 1;

                    const substeps = substepsByStep[stepId] || [];

                    const substepLetter = String.fromCharCode(
                      65 + substeps.length
                    ); // A, B, C...

                    // Calculate media counters before navigation
                    const currentAttachmentCounter = attachmentCounter;
                    const currentVideoCounter = videoCounter;
                    const currentFormCounter = formCounter;
                    const currentCanvasCounter = canvasCounter;
                    const currentRichTextCounter = richTextCounter;

                    const combinedItems = [
                      ...(richTexts[stepId] || []).map((item) => ({
                        ...item,
                        type: "richText",
                      })),
                      ...(forms[stepId] || []).map((item) => ({
                        ...item,
                        type: "form",
                      })),
                      ...(attachments[stepId] || []).map((item) => ({
                        ...item,
                        type: "attachment",
                      })),
                      ...(canvases[stepId] || []).map((item) => ({
                        ...item,
                        type: "canvas",
                      })),
                      ...(videos[stepId] || []).map((item) => ({
                        ...item,
                        type: "video",
                      })),
                    ]; // Filter out substeps or unwanted items (same as your current filter)
                    const filteredItems = combinedItems.filter(
                      (item) =>
                        !item.substep_id_ref && !item.sub_step_tree_node_id
                    );

                    filteredItems.sort((a, b) => {
                      const orderA =
                        a.sibling_order || a.procedure_tree?.sibling_order;
                      const orderB =
                        b.sibling_order || b.procedure_tree?.sibling_order;

                      const orderCompare = compareSiblingOrders(orderA, orderB);
                      if (orderCompare !== 0) return orderCompare;

                      // Fallback: compare created_at
                      const dateA = new Date(a.created_at);
                      const dateB = new Date(b.created_at);
                      return dateB - dateA; // latest comes first
                    });
                    const allChildrenUnderStep = [
                      ...(richTexts[stepId] || []),
                      ...(forms[stepId] || []),
                      ...(attachments[stepId] || []),
                      ...(canvases[stepId] || []),
                      ...(videos[stepId] || []),
                      ...(substepsByStep[stepId] || []),
                    ];

                    return (
                      <div key={stepId} style={{ marginTop: "20px" }}>
                        <label
                          style={{
                            display: "flex",
                            gap: "8px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            disabled={true}
                            checked={true}
                          />

                          <h2 style={{ margin: 0 }}>Step {globalStepNumber}</h2>
                        </label>
                        <hr />
                        <p>{step.short_description || ""}</p>
                        <p>
                          <p>
                            {structuredClone(step.step_details)
                              .sort(
                                (a, b) =>
                                  new Date(a.created_at) -
                                  new Date(b.created_at)
                              )
                              .map((myStep) => {
                                const partNumber =
                                  myStep.part_info?.part_number || "";
                                const description =
                                  myStep.part_info?.description || "";
                                const qtyText =
                                  myStep.qty && myStep.qty != 0
                                    ? `Qty ${myStep.qty}`
                                    : "";

                                const partText = partNumber
                                  ? ` (${partNumber})`
                                  : "";

                                return `${myStep.action}${partText} ${description} ${qtyText}`.trim();
                              })
                              .join(" ")}
                          </p>
                        </p>

                        <h4>
                          {step.bom_table_title_info?.category_name || ""}
                        </h4>

                        <table className="table">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>
                                {
                                  step.part_number_column_title_ref_info
                                    ?.category_name
                                }{" "}
                              </th>
                              <th>Description</th>
                              <th>Qty Per</th>
                              <th>Unit</th>
                            </tr>
                          </thead>

                          <tbody>
                            {[...step.parts_required]
                              .sort(
                                (a, b) =>
                                  new Date(a.created_at) -
                                  new Date(b.created_at)
                              )
                              .map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  <td>{row.part_info.type_info.name}</td>
                                  <td>{row.part_info.part_number}</td>
                                  <td>
                                    {row.part_info.part_description ||
                                    row.part_info.description ? (
                                      <RenderHtml
                                        content={
                                          row.part_info.part_description ||
                                          row.part_info.description
                                        }
                                      />
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td>{row.qty === 0 ? "" : row.qty}</td>
                                  <td>{row.part_info?.unit_info?.unit_name}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>

                        {/* Step Content */}

                        {filteredItems.map((item, childIndex) => {
                          if (item.substep_id_ref || item.sub_step_tree_node_id)
                            return null;
                          switch (item.type) {
                            case "richText": {
                              const globalRichTextNumber = richTextCounter++; // ✅ use then increment
                              // render richText block here
                              const label = (
                                <h2>Rich Text {globalRichTextNumber}</h2>
                              );

                              const newSiblingOrderForRichText =
                                getAdjustedSiblingOrder(
                                  item.sibling_order || "null",
                                  allChildrenUnderStep
                                );

                              return (
                                <div
                                  key={item.rich_text_tree_node_id}
                                  style={{ marginTop: "20px" }}
                                >
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={true}
                                      checked={true}
                                    />
                                    {label}
                                  </label>

                                  <div
                                    style={{
                                      marginLeft: "5px",
                                      marginTop: "-10px",
                                    }}
                                  >
                                    {item.short_description}
                                  </div>
                                  <div
                                    style={{
                                      marginLeft: "4px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    {item.category_data
                                      ? item.category_data?.category_name
                                      : item.category_info?.category_name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      marginLeft: "4px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    <div
                                      className="rich-text-content-display"
                                      style={{ marginTop: "-6px", clear: "both" }}
                                      dangerouslySetInnerHTML={{
                                        __html: item.rich_text_content,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            }
                            case "video": {
                              const globalVideoNumber = videoCounter++; // ✅ use then increment
                              const label = <h2>Video {globalVideoNumber}</h2>;

                              return (
                                <div key={label} style={{ marginTop: "20px" }}>
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={true}
                                      checked={true}
                                    />
                                    {label}
                                  </label>

                                  {renderVideo(item)}

                                  <div>{item.video_url}</div>

                                  <div>{item.short_description}</div>
                                </div>
                              );
                            }

                            case "form": {
                              const globalFormNumber = formCounter++; // ✅ use then increment
                              const label = <h2>Form {globalFormNumber}</h2>;
                              // render form block here
                              return (
                                <div key={label}>
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                      marginTop: "20px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={true}
                                      checked={true}
                                    />

                                    {label}
                                  </label>
                                  <div>{item.short_description}</div>
                                </div>
                              );
                            }
                            case "canvas": {
                              const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                              const label = (
                                <h3>Canvas {globalCanvasNumber}</h3>
                              );

                              const pages = getPages(item);
                              const shapes = item.shapes || [];

                              const newSiblingOrderForCanvas =
                                getAdjustedSiblingOrder(
                                  item.sibling_order || "null",
                                  allChildrenUnderStep
                                );

                              return (
                                <div key={label} style={{ marginTop: "40px" }}>
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={true}
                                      checked={true}
                                    />

                                    <h2>{label}</h2>
                                  </label>

                                  <div>{item.short_description}</div>

                                  {/* Display all pages vertically */}
                                  <CanvasDisplay pages={pages} />
                                </div>
                              );
                            }
                            case "attachment": {
                              const attachmentList = item.attachments || [];
                              if (attachmentList.length === 0) return null;

                              //  const globalAttachmentNumber =
                              //           attachmentCounter++; // ✅ use then increment
                              const globalAttachmentNumber =
                                attachmentCounter++; // ✅ Increment here per attachment
                              const label = (
                                <h2>Attachment {globalAttachmentNumber}</h2>
                              );
                              const newSiblingOrderForAttachment =
                                getAdjustedSiblingOrder(
                                  item.sibling_order || "null",
                                  allChildrenUnderStep
                                );

                              return (
                                <div key={item}>
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                      marginTop: "20px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={true}
                                      checked={true}
                                    />
                                    {label}
                                  </label>
                                  <div>{item.short_description}</div>
                                  {attachmentList.map((attachment, i) => (
                                    <div key={i} style={{ marginLeft: "20px" }}>
                                      {renderPreview(attachment)}
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            default:
                              return null;
                          }
                        })}

                        {/* Substeps */}
                        {(substepsByStep[stepId] || []).map(
                          (substep, substepIndex) => {
                            const substepId = substep.sub_step_tree_node_id;
                            const combinedItems = [
                              ...(richTexts[substepId] || []).map((item) => ({
                                ...item,
                                type: "richText",
                              })),
                              ...(forms[substepId] || []).map((item) => ({
                                ...item,
                                type: "form",
                              })),
                              ...(attachments[substepId] || []).map((item) => ({
                                ...item,
                                type: "attachment",
                              })),
                              ...(canvases[substepId] || []).map((item) => ({
                                ...item,
                                type: "canvas",
                              })),
                              ...(videos[substepId] || []).map((item) => ({
                                ...item,
                                type: "video",
                              })),
                            ]; // Filter out substeps or unwanted items (same as your current filter)
                            const filteredItems = combinedItems;

                            filteredItems.sort((a, b) => {
                              const orderA =
                                a.sibling_order ||
                                a.procedure_tree?.sibling_order;
                              const orderB =
                                b.sibling_order ||
                                b.procedure_tree?.sibling_order;

                              const orderCompare = compareSiblingOrders(
                                orderA,
                                orderB
                              );
                              if (orderCompare !== 0) return orderCompare;

                              // Fallback: compare created_at
                              const dateA = new Date(a.created_at);
                              const dateB = new Date(b.created_at);
                              return dateB - dateA; // latest comes first
                            });

                            // Global step number (same logic you already use for steps)
                            const stepsBeforeThisOne =
                              sortedSections
                                .slice(0, index)
                                .reduce((sectionAcc, s) => {
                                  const steps =
                                    stepsBySection[s.section_tree_node_id] ||
                                    [];
                                  return sectionAcc + steps.length;
                                }, 0) + stepIndex;

                            const globalStepNumber = stepsBeforeThisOne + 1;

                            // Final substep label like Substep 5A
                            const substepLetter = String.fromCharCode(
                              65 + substepIndex
                            ); // A, B, C, ...
                            const newSubstepLetter = String.fromCharCode(
                              65 + substepIndex + 1
                            );
                            const allChildrenUnderSubstep = [
                              ...(richTexts[substepId] || []),
                              ...(forms[substepId] || []),
                              ...(attachments[substepId] || []),
                              ...(canvases[substepId] || []),
                              ...(videos[substepId] || []),
                              ...(substepsByStep[substepId] || []),
                            ];
                            const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;
                            const newSiblingOrder = getAdjustedSiblingOrder(
                              substep.sibling_order || "null",
                              allChildrenUnderSubstep
                            );
                            const globalRichTextNumber = richTextCounter; // ✅ use then increment
                            const globalAttachmentNumber = attachmentCounter;
                            const globalVideoNumber = videoCounter;
                            const globalCanvasNumber = canvasCounter;
                            const globalFormNumber = formCounter;

                            return (
                              <div
                                key={substepId}
                                style={{ marginTop: "20px" }}
                              >
                                <div>
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={true}
                                      checked={true}
                                    />
                                    <h2 style={{ margin: 0 }}>
                                      {globalSubstepLabel}
                                    </h2>
                                  </label>

                                  <div>{substep.short_description}</div>
                                </div>

                                {/* Substep Content */}
                                {filteredItems.map((item, childIndex) => {
                                  switch (item.type) {
                                    case "richText": {
                                      const globalRichTextNumber =
                                        richTextCounter++; // ✅ use then increment
                                      // render richText block here
                                      const label = (
                                        <h2>
                                          Rich Text {globalRichTextNumber}
                                        </h2>
                                      );

                                      const newSiblingOrderForRichText =
                                        getAdjustedSiblingOrder(
                                          item.sibling_order || "null",
                                          allChildrenUnderSubstep
                                        );

                                      return (
                                        <div
                                          key={item.rich_text_tree_node_id}
                                          style={{ marginTop: "20px" }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              disabled={true}
                                              checked={true}
                                            />
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              marginLeft: "5px",
                                              marginTop: "-10px",
                                            }}
                                          >
                                            {item.short_description}
                                          </div>
                                          <div
                                            style={{
                                              marginLeft: "4px",
                                              marginTop: "5px",
                                            }}
                                          >
                                            {item.category_data
                                              ? item.category_data
                                                  ?.category_name
                                              : item.category_info
                                                  ?.category_name}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              marginLeft: "4px",
                                              marginTop: "5px",
                                            }}
                                          >
                                            <div
                                              className="rich-text-content-display"
                                              style={{ marginTop: "-6px", clear: "both" }}
                                              dangerouslySetInnerHTML={{
                                                __html: item.rich_text_content,
                                              }}
                                            />
                                          </div>
                                        </div>
                                      );
                                    }
                                    case "video": {
                                      const globalVideoNumber = videoCounter++; // ✅ use then increment
                                      const label = (
                                        <h2>Video {globalVideoNumber}</h2>
                                      );

                                      const newSiblingOrderForVideo =
                                        getAdjustedSiblingOrder(
                                          item.sibling_order || "null",
                                          allChildrenUnderSubstep
                                        );

                                      return (
                                        <div
                                          key={label}
                                          style={{ marginTop: "20px" }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              disabled={true}
                                              checked={true}
                                            />
                                            {label}
                                          </label>

                                          {renderVideo(item)}

                                          <div>{item.video_url}</div>

                                          <div>{item.short_description}</div>
                                        </div>
                                      );
                                    }

                                    case "form": {
                                      const globalFormNumber = formCounter++; // ✅ use then increment
                                      const label = (
                                        <h2>Form {globalFormNumber}</h2>
                                      );
                                      // render form block here
                                      return (
                                        <div key={label}>
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              marginTop: "20px",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              disabled={true}
                                              checked={true}
                                            />

                                            {label}
                                          </label>
                                          <div>{item.short_description}</div>
                                        </div>
                                      );
                                    }
                                    case "canvas": {
                                      const globalCanvasNumber =
                                        canvasCounter++; // ✅ use then increment
                                      const label = (
                                        <h3>Canvas {globalCanvasNumber}</h3>
                                      );
                                      const shapes = item.shapes || [];
                                      const pages = getPages(item);

                                      const newSiblingOrderForCanvas =
                                        getAdjustedSiblingOrder(
                                          item.sibling_order || "null",
                                          allChildrenUnderSubstep
                                        );

                                      return (
                                        <div
                                          key={label}
                                          style={{ marginTop: "40px" }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              disabled={true}
                                              checked={true}
                                            />

                                            <h2>{label}</h2>
                                          </label>

                                          <div>{item.short_description}</div>

                                          {/* Display all pages vertically */}
                                          <CanvasDisplay
                                            pages={pages}
                                            hasUserContent={hasUserContent}
                                            getTextStyle={getTextStyle}
                                            stripHtmlPreserveFormatting={
                                              stripHtmlPreserveFormatting
                                            }
                                            estimateKonvaTextHeight={
                                              estimateKonvaTextHeight
                                            }
                                            formatListContent={
                                              formatListContent
                                            }
                                            hasNoteContent={hasNoteContent}
                                            hasTaskContent={hasTaskContent}
                                            calculateGroupHeight={
                                              calculateGroupHeight
                                            }
                                            calculateTextBlockWidth={
                                              calculateTextBlockWidth
                                            }
                                          />
                                        </div>
                                      );
                                    }
                                    case "attachment": {
                                      const attachmentList =
                                        item.attachments || [];
                                      if (attachmentList.length === 0)
                                        return null;

                                      //  const globalAttachmentNumber =
                                      //           attachmentCounter++; // ✅ use then increment
                                      const globalAttachmentNumber =
                                        attachmentCounter++; // ✅ Increment here per attachment
                                      const label = (
                                        <h2>
                                          Attachment {globalAttachmentNumber}
                                        </h2>
                                      );
                                      const newSiblingOrderForAttachment =
                                        getAdjustedSiblingOrder(
                                          item.sibling_order || "null",
                                          allChildrenUnderSubstep
                                        );

                                      return (
                                        <div key={item}>
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              marginTop: "20px",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              disabled={true}
                                              checked={true}
                                            />
                                            {label}
                                          </label>
                                          <div>{item.short_description}</div>
                                          {attachmentList.map(
                                            (attachment, i) => (
                                              <div
                                                key={i}
                                                style={{ marginLeft: "20px" }}
                                              >
                                                {renderPreview(attachment)}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      );
                                    }
                                    default:
                                      return null;
                                  }
                                })}
                              </div>
                            );
                          }
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            );
          })}

        <div style={{ height: "100px" }}></div>
      </div>
    </div>
  );
};

export default SelectedPartPage;
